import type { UserPublic } from '@/client/types.gen';
import { buildRouteAccessPermissionMap } from '@/utils/permissionCatalog';

/**
 * @see https://umijs.org/docs/max/access#access
 * 权限控制模块
 *
 * 这个函数定义了应用中所有的权限规则
 * initialState 是从 app.tsx 的 getInitialState 函数返回的全局状态
 *
 * 权限使用方式：
 * 1. 在路由配置中使用 access 属性，如 access: 'canAdmin'
 * 2. 在组件中使用 useAccess() hook 获取权限对象
 */
export default function access(
  initialState: { currentUser?: UserPublic } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const permissionSet = new Set(currentUser?.permissions || []);
  const routeAccessPermissionMap = buildRouteAccessPermissionMap();
  const hasPermission = (...codes: string[]) =>
    Boolean(
      currentUser &&
        (currentUser.is_superuser ||
          codes.some((code) => permissionSet.has(code))),
    );
  const hasPermissionCodes = (...codes: string[]) => hasPermission(...codes);
  const hasRouteAccess = (accessName: string) =>
    hasPermission(...(routeAccessPermissionMap[accessName] || []));

  return {
    canAdmin: currentUser?.is_superuser,
    canViewWorkbench: Boolean(currentUser),
    canViewDashboard: hasRouteAccess('canViewDashboard'),
    canViewPackageMetrics: hasRouteAccess('canViewPackageMetrics'),
    canReadVersionManagement: hasPermission(
      'version_management:read',
      'version_management:write',
      'audit_config:read',
      'audit_config:write',
      'version_management.ips:read',
      'version_management.ips:write',
      'version_management.packages:read',
      'version_management.packages:write',
    ),
    canReadVersionManagementIps: hasPermission(
      'version_management.ips:read',
      'version_management.ips:write',
    ),
    canReadVersionManagementPackages: hasPermission(
      'version_management.packages:read',
      'version_management.packages:write',
    ),
    canReadContent: hasRouteAccess('canReadContent'),
    canReadComicReviews: hasRouteAccess('canReadComicReviews'),
    canWriteContent: hasRouteAccess('canWriteContent'),
    canReadTags: hasRouteAccess('canReadTags'),
    canWriteTags: hasRouteAccess('canWriteTags'),
    canReadLogs: hasRouteAccess('canReadLogs'),
    canReadLogsAppUsers: hasRouteAccess('canReadLogsAppUsers'),
    canReadLogsBlackRecords: hasRouteAccess('canReadLogsBlackRecords'),
    canReadLogsSearchRecords: hasRouteAccess('canReadLogsSearchRecords'),
    canReadLogsComicPushDeliveries: hasRouteAccess(
      'canReadLogsComicPushDeliveries',
    ),
    canReadLogsWhitelist: hasRouteAccess('canReadLogsWhitelist'),
    canReadLogsUserPolicies: hasRouteAccess('canReadLogsUserPolicies'),
    canReadLogsUserPolicyHits: hasRouteAccess('canReadLogsUserPolicyHits'),
    canWriteLogs: hasRouteAccess('canWriteLogs'),
    canReadAuditConfig: hasRouteAccess('canReadAuditConfig'),
    canReadGrowth: hasRouteAccess('canReadGrowth'),
    canWriteGrowth: hasRouteAccess('canWriteGrowth'),
    canReadGrowthAdSlots: hasRouteAccess('canReadGrowthAdSlots'),
    canReadAdManagement: hasRouteAccess('canReadAdManagement'),
    canReadAdGroups: hasRouteAccess('canReadAdGroups'),
    canReadGlobalAdConfigs: hasRouteAccess('canReadGlobalAdConfigs'),
    canReadAdIds: hasRouteAccess('canReadAdIds'),
    canReadAdTypes: hasRouteAccess('canReadAdTypes'),
    canReadAdSlotPositions: hasRouteAccess('canReadAdSlotPositions'),
    canReadAdClientPositions: hasRouteAccess('canReadAdClientPositions'),
    canReadAdPlacementSettings: hasRouteAccess('canReadAdPlacementSettings'),
    canReadAdExperiments: hasRouteAccess('canReadAdExperiments'),
    canReadReadingEvents: hasRouteAccess('canReadReadingEvents'),
    canWriteGrowthAdSlots: hasRouteAccess('canWriteGrowthAdSlots'),
    canReadGrowthHomeGuide: hasRouteAccess('canReadGrowthHomeGuide'),
    canWriteGrowthHomeGuide: hasRouteAccess('canWriteGrowthHomeGuide'),
    canReadGrowthAppDecor: hasRouteAccess('canReadGrowthAppDecor'),
    canWriteGrowthAppDecor: hasRouteAccess('canWriteGrowthAppDecor'),
    canReadGrowthAppDecorProfiles: hasRouteAccess(
      'canReadGrowthAppDecorProfiles',
    ),
    canReadGrowthAppDecorPools: hasRouteAccess('canReadGrowthAppDecorPools'),
    canReadGrowthAppDecorComicLists: hasRouteAccess(
      'canReadGrowthAppDecorComicLists',
    ),
    canReadGrowthFeedbacks: hasRouteAccess('canReadGrowthFeedbacks'),
    canWriteGrowthFeedbacks: hasRouteAccess('canWriteGrowthFeedbacks'),
    canReadConfigManagement: hasRouteAccess('canReadConfigManagement'),
    canReadConfigManagementGlobal: hasRouteAccess(
      'canReadConfigManagementGlobal',
    ),
    canReadConfigManagementFeatureFlags: hasRouteAccess(
      'canReadConfigManagementFeatureFlags',
    ),
    canReadConfigManagementLab: hasRouteAccess('canReadConfigManagementLab'),
    canReadUsers: hasRouteAccess('canReadUsers'),
    canReadTenants: hasRouteAccess('canReadTenants'),
    canWriteUsers: hasRouteAccess('canWriteUsers'),
    canReadGroups: hasRouteAccess('canReadGroups'),
    canWriteGroups: hasRouteAccess('canWriteGroups'),
    canWriteGroupPermissions: hasRouteAccess('canWriteGroupPermissions'),
    canReadPermissions: hasRouteAccess('canReadPermissions'),
    canReadActionLogs: hasRouteAccess('canReadActionLogs'),
    canReadSystemFlower: hasRouteAccess('canReadSystemFlower'),
    canReadSystemCache: hasRouteAccess('canReadSystemCache'),
    canReadSystemEncryptedDomains: hasRouteAccess(
      'canReadSystemEncryptedDomains',
    ),
    canReadSystemPolicyGatewayTestRoutes: hasRouteAccess(
      'canReadSystemPolicyGatewayTestRoutes',
    ),
    canAccessMaintenance: hasRouteAccess('canAccessMaintenance'),
    canReadMaintenanceReaderBugs: hasRouteAccess(
      'canReadMaintenanceReaderBugs',
    ),
    hasPermissionCodes,
    canAccessSystem: hasRouteAccess('canAccessSystem'),
  };
}
